import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {assert} from 'Asserts';
import getTestProps from 'test/lib/cypress/getTestProps';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import {ButtonSize} from 'ui-library/buttons/Button';
import HorizontalChipListField from 'ui-library/chips/HorizontalChipListField';
import {ControlledInputProps} from 'ui-library/hooks/useFormInput';
import RadioButtonGroup from 'ui-library/inputs/RadioButtonGroup';
import ToggleGroup from 'ui-library/ToggleGroup';

import ExistingWorklistFormGroup from './ExistingWorklistFormGroup';
import NewWorklistForm, {useNewWorklistForm} from './NewWorklistForm';
import useSelectedWorklistId from './useSelectedWorklistId';
import {dispatchAddWorklistItems, dispatchCreateWorklist, RefreshOptions} from './WorklistActions';
import {WorklistFormType, WorklistSummary, WorklistType} from './WorklistConstants';

export interface AddToWorklistProps {
  ids: Array<string>;
  refreshOptions: RefreshOptions;
  worklistType: WorklistType;
  useFullWidthToggle?: boolean;
}

export interface AddToWorklistFormProps {
  autoFocus?: boolean;
  newOrExisting: WorklistFormType | null;
  newWorklistName: ControlledInputProps;
  newWorklistOwnerId: string;
  setNewOrExisting: React.Dispatch<React.SetStateAction<WorklistFormType | null>>;
  updateWorklistId: (worklistSummary: WorklistSummary | null) => void;
  chipsAreLinks?: boolean;
  required?: boolean;
}

export enum WorklistFormSelectorType {
  TOGGLE_GROUP = 'TOGGLE_GROUP',
  RADIO_GROUP = 'RADIO_GROUP',
}

type WorklistFormSelectorProps =
  | {selectorType: typeof WorklistFormSelectorType.TOGGLE_GROUP}
  | {selectorType: typeof WorklistFormSelectorType.RADIO_GROUP; radioGroupLabelText: string};

export interface AddToWorklistSubmitProps {
  addItems: (ids: Array<string>) => void;
  isDisabled: boolean;
  selectedWorklistId: string | null;
}

export const useAddToWorklistForm = (
  props: AddToWorklistProps,
  initialNewOrExistingValue: WorklistFormType | null = WorklistFormType.NEW
): AddToWorklistFormProps & AddToWorklistSubmitProps => {
  const [selectedWorklistId, updateWorklistId] = useSelectedWorklistId();
  const [newOrExisting, setNewOrExisting] = useState<WorklistFormType | null>(initialNewOrExistingValue);
  const newWorklistFormProps = useNewWorklistForm();

  function addItems(ids: Array<string>): void {
    if (newOrExisting === WorklistFormType.NEW) {
      dispatchCreateWorklist(newWorklistFormProps.name.value, props.worklistType, ids);
    } else if (newOrExisting === WorklistFormType.EXISTING) {
      if (selectedWorklistId) {
        dispatchAddWorklistItems(selectedWorklistId, ids, props.refreshOptions);
      }
    } else {
      assert(false, 'Unexpected worklist form type');
    }
  }

  const isDisabled =
    newOrExisting == null ||
    (newOrExisting === WorklistFormType.EXISTING && !selectedWorklistId) ||
    (newOrExisting === WorklistFormType.NEW && !newWorklistFormProps.name.value);

  return {
    updateWorklistId,
    newOrExisting,
    setNewOrExisting,
    newWorklistName: newWorklistFormProps.name,
    newWorklistOwnerId: newWorklistFormProps.ownerId,
    addItems,
    isDisabled,
    selectedWorklistId,
  };
};

export default function AddToWorklistForm(
  props: AddToWorklistProps & AddToWorklistFormProps & WorklistFormSelectorProps
): JSX.Element {
  return (
    <div {...getTestProps({component: 'AddToWorklistForm', element: 'root'})}>
      <Row className={css(styles.spacedRow)}>
        <Col sm={props.useFullWidthToggle ? 12 : 6}>
          {props.selectorType === WorklistFormSelectorType.RADIO_GROUP ? (
            <RadioButtonGroup<WorklistFormType>
              title={props.radioGroupLabelText}
              options={[
                {
                  value: WorklistFormType.NEW,
                  label: 'New worklist',
                  dataTestAttrs: {
                    component: 'AddToWorklistForm',
                    element: 'RadioButtonGroup',
                    key: WorklistFormType.NEW,
                  },
                },
                {
                  value: WorklistFormType.EXISTING,
                  label: 'Existing worklist',
                  dataTestAttrs: {
                    component: 'AddToWorklistForm',
                    element: 'RadioButtonGroup',
                    key: WorklistFormType.EXISTING,
                  },
                },
              ]}
              selectedValue={props.newOrExisting}
              onChange={(value: WorklistFormType) => {
                props.setNewOrExisting(value);
              }}
              onClick={(value: WorklistFormType) => {
                if (value === props.newOrExisting) {
                  props.setNewOrExisting(null);
                }
              }}
              hasBottomSpacing={false}
            />
          ) : (
            <ToggleGroup<WorklistFormType>
              options={[
                {
                  id: WorklistFormType.NEW,
                  buttonProps: {text: 'New worklist'},
                  dataTestAttrs: {
                    component: 'AddToWorklistForm',
                    element: 'ToggleGroup',
                    key: WorklistFormType.NEW,
                  },
                },
                {
                  id: WorklistFormType.EXISTING,
                  buttonProps: {text: 'Existing worklist'},
                  dataTestAttrs: {
                    component: 'AddToWorklistForm',
                    element: 'ToggleGroup',
                    key: WorklistFormType.EXISTING,
                  },
                },
              ]}
              selection={props.newOrExisting ?? WorklistFormType.NEW}
              onChangeSelection={(value: WorklistFormType) => {
                props.setNewOrExisting(value);
              }}
              size={ButtonSize.SMALL}
              isFullWidth={true}
            />
          )}
        </Col>
      </Row>
      {props.newOrExisting === WorklistFormType.EXISTING && (
        <ExistingWorklistFormGroup
          label="Select worklist"
          worklistTypes={[props.worklistType]}
          onChange={props.updateWorklistId}
          autoFocus={props.autoFocus}
          required={props.required}
        />
      )}
      {props.newOrExisting === WorklistFormType.NEW && (
        <NewWorklistForm name={props.newWorklistName} ownerId={props.newWorklistOwnerId} />
      )}
      {props.ids.length > 0 && (
        <HorizontalChipListField ids={props.ids} title="Selected items" chipsAreLinks={props.chipsAreLinks} />
      )}
    </div>
  );
}

AddToWorklistForm.defaultProps = {
  useFullWidthToggle: false,
};

const styles = StyleSheet.create({
  spacedRow: {
    marginBottom: 10,
  },
});
